<template>
  <div class="replay-page">
    <!-- 主要内容区：左侧控制面板 + 右侧地图与进度条 -->
    <div class="main-content">
      <!-- ====== 左侧控制面板 ====== -->
      <div class="left-panel">
        <!-- 车辆标题 -->
        <div class="vehicle-title">
          <span class="main-title">{{ title }} 历史轨迹</span>
        </div>

        <!-- 顶部筛选栏 -->
        <div class="filter-bar">
          <div class="filter-left">
            <span class="filter-label">公历</span>
            <el-select v-model="selectedYear" size="mini" @change="onYearChange" style="width:90px;">
              <el-option v-for="y in yearOptions" :key="y" :label="y + '年'" :value="y" />
            </el-select>
            <el-select v-model="selectedMonth" size="mini" @change="onMonthChange" style="width:80px;">
              <el-option v-for="m in 12" :key="m" :label="m + '月'" :value="m" />
            </el-select>

          </div>
          <div class="filter-right">
            <el-button size="mini" type="primary" @click="downloadTrajectory">
              下载轨迹
            </el-button>
          </div>
          <!-- <div class="filter-right">
            <el-button size="mini" plain @click="quickDate('today')">今天</el-button>
            <el-button size="mini" plain @click="quickDate('yesterday')">昨天</el-button>
            <el-button size="mini" plain @click="quickDate('week')">最近7天</el-button>
            <el-button size="mini" plain @click="quickDate('month')">本月</el-button>
          </div> -->
        </div>

        <!-- 平铺日历（带每日里程） -->
        <div class="calendar-wrapper">
          <el-calendar v-model="calendarDate" @pick="onCalendarChange" @change="onCalendarChange">
            <!-- 自定义头部（隐藏默认，使用自定义） -->
            <template slot="header" slot-scope="{ date }">
              <div class="calendar-header">
                <span class="calendar-title">公历 {{ date.getFullYear() }} 年 {{ date.getMonth() + 1 }} 月</span>
              </div>
            </template>
            <!-- 自定义日期单元格 -->
            <template slot="dateCell" slot-scope="{ date, data }">
              <div class="calendar-day-cell" :class="{ 'is-selected': data.isSelected }"
                @click="handleDayClick(date, data)">
                <div class="day-number">{{ date.getDate() }}</div>
                <div class="day-mileage" v-if="dailyMileage[data.day] !== undefined">
                  {{ dailyMileage[data.day] }}
                </div>
                <div class="day-mileage" v-else>--</div>
              </div>
            </template>
          </el-calendar>
        </div>

        <!-- 时间选择器 -->
        <div class="time-picker-group">
          <div class="time-item">
            <label>开始时间</label>
            <el-date-picker v-model="startTime" type="datetime" placeholder="选择开始时间" value-format="yyyy-MM-dd HH:mm:ss"
              :picker-options="pickerOpts" size="small" />
          </div>
          <div class="time-item">
            <label>结束时间</label>
            <el-date-picker v-model="endTime" type="datetime" placeholder="选择结束时间" value-format="yyyy-MM-dd HH:mm:ss"
              :picker-options="pickerOpts" size="small" />
          </div>
          <el-button type="primary" size="small" icon="el-icon-search" @click="fetchTrajectory" :loading="loading"
            style="width:100%;">
            查询
          </el-button>
        </div>

        <!-- 参数设置 & 回放开关 -->
        <!-- <div class="params-group">
          <div class="param-item replay-switch">
            <span class="param-label">轨迹回放</span>
            <el-switch v-model="isReplayMode" size="small" @change="onReplayModeChange" />
          </div>
        </div> -->
      </div>

      <!-- ====== 右侧区域：地图 + 进度条 ====== -->
      <div class="right-area">
        <!-- 地图容器 -->
        <div class="map-container">
          <amap-container ref="mapComp" :path-data="pathData" />
          <!-- 图例 -->
          <div class="map-legend">
            <span><span class="dot start-dot"></span>起点</span>
            <span><span class="dot end-dot"></span>终点</span>
            <span><span class="line-sample"></span>轨迹</span>
          </div>
        </div>

        <!-- 回放进度条 -->
        <div class="replay-bar" v-if="isReplayMode && hasData">
          <div class="replay-controls">
            <el-button :icon="isPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'" circle size="mini"
              @click="togglePlay" />
            <span class="time-label">{{ currentTimeStr }}</span>
            <el-slider v-model="progress" :format-tooltip="val => val + '%'" @change="onProgressChange"
              style="flex:1; margin:0 12px;" />
            <span class="time-label">{{ totalTimeStr }}</span>
            <span class="speed-label">倍速</span>
            <el-select v-model="playSpeed" size="mini" style="width:78px;">
              <el-option label="0.2×" :value="0.2" />
              <el-option label="0.5×" :value="0.5" />
              <el-option label="1×" :value="1" />
              <el-option label="2×" :value="2" />
              <el-option label="4×" :value="4" />
            </el-select>
          </div>
        </div>
      </div>
    </div>

    <!-- ====== 底部表格 ====== -->
    <div class="table-wrapper">
      <div class="table-header">
        <span class="table-title">历史轨迹</span>
      </div>
      <el-table :data="filteredTableData" border stripe size="small"
        :header-cell-style="{ 'background-color': '#E8ECEF', 'color': '#333', 'height': '50px' }" style="width:100%;">
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="plate" label="车牌号码" width="110" align="center" />
        <el-table-column prop="timeStr" label="时间" width="170" align="center" sortable />
        <el-table-column prop="location" label="位置" min-width="150" align="center" show-overflow-tooltip />
        <el-table-column prop="replayMileage" label="总里程(km)" width="120" align="center" />
        <el-table-column prop="status" label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === '行驶中' ? 'success' : scope.row.status === '停止' ? 'warning' : 'info'"
              size="mini">
              {{ scope.row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="speed" label="速度(km/h)" width="150" align="center" />
        <el-table-column prop="longitude" label="经度" width="110" align="center" />
        <el-table-column prop="latitude" label="纬度" width="110" align="center" />
      </el-table>
      <div class="table-footer">
        <span>共 {{ filteredTableData.length }} 条记录</span>
      </div>
    </div>
  </div>
</template>

<script>
// 引入高德地图 JSAPI 加载器
import AMapLoader from '@amap/amap-jsapi-loader';

// ---- 地图子组件（使用 AMapLoader 异步加载） ----
const AmapContainer = {
  name: 'AmapContainer',
  props: {
    pathData: { type: Array, default: () => [] },
    mapType: { type: String, default: '2d' },
  },
  data() {
    return {
      map: null,
      polyline: null,
      marker: null,
      startMarker: null,
      endMarker: null,
      mapReady: false,
      AMap: null,
    };
  },
  watch: {
    pathData: {
      handler(val) {
        if (val && val.length > 0 && this.mapReady) {
          this.drawTrajectory(val);
        }
      },
      immediate: true,
    },
    mapType(type) {
      if (this.mapReady) this.switchMapType(type);
    },
  },
  mounted() {
    this.loadMap();
  },
  beforeDestroy() {
    if (this.map) {
      this.map.destroy();
      this.map = null;
    }
  },
  methods: {
    async loadMap() {
      try {
        const AMap = await AMapLoader.load({
          key: '0faeff35413697ab916e270235874fc9',
          version: '2.0',
          plugins: ['AMap.ToolBar', 'AMap.Scale'],
        });
        this.AMap = AMap;
        this.initMap(AMap);
      } catch (error) {
        console.error('高德地图加载失败:', error);
        this.$message.error('地图加载失败，请检查网络或Key');
      }
    },
    initMap(AMap) {
      if (!this.$el) return;
      this.map = new AMap.Map(this.$el, {
        zoom: 14,
        center: [108.94, 34.26],
        viewMode: '2D',
        // 替换为深色样式（高德官方深色风格）
        mapStyle: 'amap://styles/3c7c6f3e308aac6a4b21eefc888053b9',
        resizeEnable: true,
      });
      this.map.addControl(new AMap.ToolBar({ position: 'RT' }));
      this.map.addControl(new AMap.Scale());
      this.mapReady = true;
      if (this.pathData && this.pathData.length > 0) {
        this.drawTrajectory(this.pathData);
      }
    },
    switchMapType(type) {
      if (!this.map) return;
      if (type === '2d') {
        this.map.setViewMode('2D');
        this.map.setMapStyle('amap://styles/3c7c6f3e308aac6a4b21eefc888053b9'); // 深色
      } else if (type === '3d') {
        this.map.setViewMode('3D');
        this.map.setMapStyle('amap://styles/3c7c6f3e308aac6a4b21eefc888053b9'); // 深色
      } else if (type === 'satellite') {
        this.map.setViewMode('2D');
        this.map.setMapStyle('amap://styles/6a61e8d0c9a4639d5d7d13f22d7f2868');
      }
    },
    drawTrajectory(pathData) {
      this.clearOverlays();
      if (!this.map || !pathData || pathData.length < 2) return;
      const AMap = this.AMap;
      if (!AMap) return;
      const path = pathData.map(p => [p.longitude, p.latitude]);
      this.polyline = new AMap.Polyline({
        path,
        strokeColor: '#3b7cff',
        strokeWeight: 5,
        strokeStyle: 'solid',
        lineJoin: 'round',
        lineCap: 'round',
      });
      this.polyline.setMap(this.map);

      this.startMarker = new AMap.Marker({
        position: path[0],
        icon: new AMap.Icon({
          size: new AMap.Size(28, 28),
          image: 'https://webapi.amap.com/theme/v1.3/markers/n/start.png',
          imageSize: new AMap.Size(28, 28),
        }),
        label: { content: '起点', direction: 'top', offset: new AMap.Pixel(0, -4) },
      });
      this.startMarker.setMap(this.map);

      this.endMarker = new AMap.Marker({
        position: path[path.length - 1],
        icon: new AMap.Icon({
          size: new AMap.Size(28, 28),
          image: 'https://webapi.amap.com/theme/v1.3/markers/n/end.png',
          imageSize: new AMap.Size(28, 28),
        }),
        label: { content: '终点', direction: 'bottom', offset: new AMap.Pixel(0, 4) },
      });
      this.endMarker.setMap(this.map);

      this.marker = new AMap.Marker({
        position: path[0],
        icon: new AMap.Icon({
          size: new AMap.Size(40, 40),
          // image: 'https://a.amap.com/jsapi_demos/static/demo-center/icons/car.png',
          image: require('@/assets/img/fleet011.png'),
          imageSize: new AMap.Size(40, 40),
        }),
        autoRotation: true,
        offset: new AMap.Pixel(-20, -20),
      });
      this.marker.setMap(this.map);

      this.map.setFitView([this.polyline, this.startMarker, this.endMarker]);
    },
    clearOverlays() {
      if (this.polyline) { this.map.remove(this.polyline); this.polyline = null; }
      if (this.marker) { this.map.remove(this.marker); this.marker = null; }
      if (this.startMarker) { this.map.remove(this.startMarker); this.startMarker = null; }
      if (this.endMarker) { this.map.remove(this.endMarker); this.endMarker = null; }
    },
    updateMarkerPosition(longitude, latitude) {
      if (this.marker) {
        this.marker.setPosition([longitude, latitude]);
      }
    },
    updateTrajectory(pathData) {
      this.drawTrajectory(pathData);
    },
  },
  render() {
    return <div style="width:100%; height:100%;"></div>;
  },
};

// ---- 主组件 ----
export default {
  name: 'TrajectoryReplay',
  components: {
    AmapContainer,
  },
  data() {
    return {
      // 日历相关
      calendarDate: new Date(),
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      yearOptions: [],
      dailyMileage: {}, // { '2026-07-23': 296.5, ... }
      tableData: [],     // 新增：表格数据（后端原始顺序）
      replayData: [],    // 新增：回放数据（从早到晚）
      mapType: '2d',
      rawData: [],
      pathData: [],
      hasData: false,
      title: '',
      loading: false,
      startTime: null,
      endTime: null,
      pickerOpts: {
        shortcuts: [
          {
            text: '今天',
            onClick(picker) {
              const now = new Date();
              const s = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
              picker.$emit('pick', s);
            },
          },
          {
            text: '昨天',
            onClick(picker) {
              const now = new Date();
              const s = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
              picker.$emit('pick', s);
            },
          },
          {
            text: '最近7天',
            onClick(picker) {
              const now = new Date();
              const s = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0);
              picker.$emit('pick', s);
            },
          },
        ],
      },

      isReplayMode: true,
      isPlaying: false,
      progress: 0,
      playSpeed: 1,
      animationTimer: null,
      parkingThreshold: 30,
      showSettings: false,

      searchKeyword: '',
      sortProp: '',
      sortOrder: '',
      defaultSort: { prop: 'timeStr', order: 'descending' },  // 新增
      currentTimeStr: '--:--:--',
      totalTimeStr: '--:--:--',
    };
  },
  computed: {
    filteredTableData() {
      let data = [...this.tableData]; // 使用 tableData 而非 pathData
      if (this.searchKeyword) {
        const kw = this.searchKeyword.toLowerCase();
        data = data.filter(
          (item) =>
            item.plate.toLowerCase().includes(kw) ||
            item.location.toLowerCase().includes(kw)
        );
      }
      return data;
    },
    totalDuration() {
      if (!this.hasData || this.replayData.length < 2) return '00:00:00';
      const first = this.replayData[0].time;
      const last = this.replayData[this.replayData.length - 1].time;
      const diff = Math.floor((last - first) / 1000);
      const h = String(Math.floor(diff / 3600)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const s = String(diff % 60).padStart(2, '0');
      return `${h}:${m}:${s}`;
    },
  },
  watch: {
    isReplayMode(val) {
      if (!val) this.stopAnimation();
    },
  },
  mounted() {
    const { url, title } = this.$route.query;
    this.url = url;
    this.title = title;
    console.log(title);

    // 初始化年份选项
    const currentYear = new Date().getFullYear();
    this.yearOptions = [];
    for (let y = currentYear - 10; y <= currentYear; y++) {
      this.yearOptions.push(y);
    }


    const now = new Date();
    const s = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const e = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    this.startTime = this.formatDateTime(s);
    this.endTime = this.formatDateTime(e);

    // 获取当月每日里程
    this.fetchMonthData(this.selectedYear, this.selectedMonth);

    this.generateMockData();
    this.fetchTrajectory();
  },
  beforeDestroy() {
    this.stopAnimation();
  },
  methods: {
    // ---------- 日历相关方法 ----------
    fetchMonthData(year, month) {
      // 调用后台接口获取每日里程
      this.post(
        `car/getDailyMileage`,
        {
          license: this.title,   // 车牌号
          year: year,
          month: month,
        },
        (res) => {
          if (res.msg === 'success') {
            // 假设后端返回数据格式为 { '2026-07-01': 123.4, '2026-07-02': 456.7, ... }
            this.dailyMileage = res.data || {};
          } else {
            console.warn('获取每日里程失败:', res.msg);
            this.dailyMileage = {}; // 清空，避免显示旧数据
          }
        },
        (err) => {
          console.error('请求每日里程接口失败:', err);
          this.dailyMileage = {};
          this.$message.error('获取每日里程数据失败');
        }
      );
    },
    onYearChange() {
      this.calendarDate = new Date(this.selectedYear, this.selectedMonth - 1, 1);
      this.fetchMonthData(this.selectedYear, this.selectedMonth);
    },
    onMonthChange() {
      this.calendarDate = new Date(this.selectedYear, this.selectedMonth - 1, 1);
      this.fetchMonthData(this.selectedYear, this.selectedMonth);
    },
    prevMonth() {
      const d = new Date(this.calendarDate);
      d.setMonth(d.getMonth() - 1);
      this.calendarDate = d;
      this.selectedYear = d.getFullYear();
      this.selectedMonth = d.getMonth() + 1;
      this.fetchMonthData(this.selectedYear, this.selectedMonth);
    },
    nextMonth() {
      const d = new Date(this.calendarDate);
      d.setMonth(d.getMonth() + 1);
      this.calendarDate = d;
      this.selectedYear = d.getFullYear();
      this.selectedMonth = d.getMonth() + 1;
      this.fetchMonthData(this.selectedYear, this.selectedMonth);
    },
    handleDayClick(date, data) {
      console.log('点击日期:', date, data);
      // 调用相同的逻辑
      this.onCalendarChange(date);
    },
    onCalendarChange(val) {
      // 兼容不同版本的 el-calendar 事件参数
      let selectedDate = val;

      // 如果 val 不是 Date 对象，尝试从对象中提取
      if (!(selectedDate instanceof Date)) {
        if (selectedDate && typeof selectedDate === 'object') {
          // 某些版本返回 { date: Date, data: { isSelected: true } }
          if (selectedDate.date instanceof Date) {
            selectedDate = selectedDate.date;
          }
          // 如果对象有 day 属性（字符串格式 'YYYY-MM-DD'）
          else if (selectedDate.day) {
            selectedDate = new Date(selectedDate.day);
          }
          // 如果对象有 value 属性
          else if (selectedDate.value instanceof Date) {
            selectedDate = selectedDate.value;
          }
        }
        // 如果仍然无效，使用 calendarDate 作为备选
        if (!(selectedDate instanceof Date) && this.calendarDate instanceof Date) {
          selectedDate = this.calendarDate;
        }
        // 最后的备选
        if (!(selectedDate instanceof Date)) {
          selectedDate = new Date();
        }
      }

      console.log('选中的日期:', selectedDate);

      const start = new Date(selectedDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(selectedDate);
      end.setHours(23, 59, 59, 999);
      this.startTime = this.formatDateTime(start);
      this.endTime = this.formatDateTime(end);

      // 同步更新年份/月份下拉框
      this.selectedYear = selectedDate.getFullYear();
      this.selectedMonth = selectedDate.getMonth() + 1;

      // 自动查询轨迹
      this.fetchTrajectory();
    },

    // 快捷日期
    quickDate(type) {
      const now = new Date();
      let start, end;
      switch (type) {
        case 'today':
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
          break;
        case 'yesterday':
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
          break;
        case 'week':
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0);
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
          break;
        case 'month':
          start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
          end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
          break;
        default:
          return;
      }
      this.startTime = this.formatDateTime(start);
      this.endTime = this.formatDateTime(end);
      this.calendarDate = start;
      this.selectedYear = start.getFullYear();
      this.selectedMonth = start.getMonth() + 1;
      this.fetchTrajectory();
    },
    downloadTrajectory() {
      if (!this.pathData || this.pathData.length === 0) {
        this.$message.warning('当前无轨迹数据可下载，请先查询');
        return;
      }

      const headers = [
        '序号', '车牌号码', '时间', '位置',
        '总里程(km)', '状态', '速度(km/h)', '经度', '纬度'
      ];

      const rows = this.pathData.map((item, index) => ({
        '序号': index + 1,
        '车牌号码': item.plate || '',
        '时间': item.timeStr || '',
        '位置': item.location || '',
        '总里程(km)': item.replayMileage || 0,
        '状态': item.status || '',
        '速度(km/h)': item.speed || 0,
        '经度': item.longitude || '',
        '纬度': item.latitude || ''
      }));

      const wb = this.$XLSX.utils.book_new();
      const ws = this.$XLSX.utils.json_to_sheet(rows, { header: headers });

      ws['!cols'] = [
        { wch: 8 }, { wch: 14 }, { wch: 22 }, { wch: 20 },
        { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }
      ];

      this.$XLSX.utils.book_append_sheet(wb, ws, '轨迹数据');
      const fileName = `轨迹_${this.title || '车辆'}_${this.startTime || '开始'}_至_${this.endTime || '结束'}.xlsx`;
      this.$XLSX.writeFile(wb, fileName);
      this.$message.success('轨迹数据导出成功');
    },
    // ---------- 原有方法 ----------
    formatDateTime(date) {
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },
    switchMapType(type) {
      this.mapType = type;
    },
    generateMockData() {
      console.log(this.startTime, 'this.startTime');
      this.post(
        `car/platenosearch`,
        {
          license: this.title,
          start_time: this.startTime,
          end_time: this.endTime,
        },
        (res) => {
          if (res.msg === 'success') {
            const rawPoints = res.data;
            console.log('原始轨迹点数量:', rawPoints.length);

            if (!rawPoints || rawPoints.length < 2) {
              this.$message.warning('轨迹点数据不足');
              this.hasData = false;
              this.tableData = [];
              this.replayData = [];
              this.pathData = [];
              if (this.$refs.mapComp) {
                this.$refs.mapComp.updateTrajectory([]);
              }
              return;
            }

            const points = rawPoints.map((item) => {
              let timeStr = item.report_time;
              const time = new Date(timeStr).getTime();
              return {
                time: time,
                longitude: parseFloat(item.longitude),
                latitude: parseFloat(item.latitude),
                speed: parseFloat(item.speed) || 0,
                status: parseFloat(item.speed) > 0 ? '行驶中' : '停止',
                location: item.formatted_address || '未知',
                plate: item.plate_no || this.title,
                replayMileage: parseFloat(item.mileage) || 0,
                totalMileage: parseFloat(item.mileage) || 0,
                timeStr: timeStr,
              };
            });

            // 直接使用后端返回顺序（倒序）作为表格数据
            this.tableData = points;
            // 回放数据：升序
            this.replayData = [...points].sort((a, b) => a.time - b.time);
            this.pathData = this.replayData;

            console.log('处理后轨迹点数量:', this.tableData.length);
            this.fetchTrajectory();
          }
        },
        (err) => {
          console.log(err);
          this.$message({
            type: 'error',
            message: '获取数据失败，请稍后重试！',
          });
        }
      );
    },
    fetchTrajectory() {
      if (!this.startTime || !this.endTime) {
        this.$message.warning('请选择时间范围');
        return;
      }

      this.loading = true;

      this.post(
        `car/platenosearch`,
        {
          license: this.title,
          start_time: this.startTime,
          end_time: this.endTime,
        },
        (res) => {
          this.loading = false;
          if (res.msg === 'success') {
            const data = res.data;
            if (!data || data.length < 2) {
              this.$message.warning('该时间段内无轨迹数据');
              this.hasData = false;
              this.tableData = [];
              this.replayData = [];
              this.pathData = [];
              if (this.$refs.mapComp) {
                this.$refs.mapComp.updateTrajectory([]);
              }
              return;
            }

            const points = data.map((item) => ({
              ...item,
              longitude: parseFloat(item.longitude),
              latitude: parseFloat(item.latitude),
              time: new Date(item.report_time).getTime(),
              speed: parseFloat(item.speed) || 0,
              status:
                parseFloat(item.speed) < 1
                  ? '停止'
                  : parseFloat(item.speed) < 15
                    ? '缓行'
                    : '行驶中',
              location: item.formatted_address || '未知',
              plate: item.plate_no || this.title,
              timeStr: item.report_time,
              replayMileage: item.mileage ? parseFloat(item.mileage) : 0,
              totalMileage: 0,
            }));

            // ⭐ 表格数据：保持后端返回的原始顺序（倒序）
            this.tableData = points;

            // ⭐ 地图/回放数据：按时间升序（从早到晚）
            this.replayData = [...points].sort((a, b) => a.time - b.time);
            // 同步给 pathData，用于地图组件
            this.pathData = this.replayData;

            this.hasData = true;
            this.progress = 0;

            // 开始时间（最早），结束时间（最晚）
            this.currentTimeStr = this.formatTime(this.replayData[0].time);
            this.totalTimeStr = this.formatTime(this.replayData[this.replayData.length - 1].time);

            // 地图使用从早到晚的数据（通过 pathData 自动更新）
            // 同时调用 updateTrajectory 确保地图更新
            if (this.$refs.mapComp) {
              this.$refs.mapComp.updateTrajectory(this.replayData);
            }

            this.$message.success(`加载 ${points.length} 个轨迹点`);
          } else {
            this.$message.error(res.msg || '获取数据失败');
          }
        },
        (err) => {
          this.loading = false;
          console.error(err);
          this.$message.error('网络请求失败，请稍后重试！');
        }
      );
    },

    updateMarkerByProgress(progress) {
      if (!this.hasData || this.replayData.length === 0) return;
      const index = (progress / 100) * (this.replayData.length - 1);
      const curIdx = Math.floor(index);
      const nextIdx = Math.min(curIdx + 1, this.replayData.length - 1);
      const frac = index - curIdx;
      const cur = this.replayData[curIdx];
      const next = this.replayData[nextIdx];
      const longitude = cur.longitude + (next.longitude - cur.longitude) * frac;
      const latitude = cur.latitude + (next.latitude - cur.latitude) * frac;
      if (this.$refs.mapComp) {
        this.$refs.mapComp.updateMarkerPosition(longitude, latitude);
      }
      const time = cur.time + (next.time - cur.time) * frac;
      this.currentTimeStr = this.formatTime(time);
    },

    togglePlay() {
      if (this.isPlaying) {
        this.stopAnimation();
      } else {
        this.startAnimation();
      }
    },
    startAnimation() {
      if (this.isPlaying || !this.hasData) return;
      if (this.progress >= 100) {
        this.progress = 0;
        this.updateMarkerByProgress(0);
      }
      this.isPlaying = true;
      const step = () => {
        if (!this.isPlaying) return;
        const inc = 0.4 * this.playSpeed;
        let newProg = this.progress + inc;
        if (newProg >= 100) {
          newProg = 100;
          this.updateMarkerByProgress(newProg);
          this.progress = newProg;
          this.stopAnimation();
          this.$message.info('回放结束');
          return;
        }
        this.progress = newProg;
        this.updateMarkerByProgress(this.progress);
        this.animationTimer = requestAnimationFrame(step);
      };
      this.animationTimer = requestAnimationFrame(step);
    },
    stopAnimation() {
      this.isPlaying = false;
      if (this.animationTimer) {
        cancelAnimationFrame(this.animationTimer);
        this.animationTimer = null;
      }
    },
    onProgressChange(val) {
      if (this.isPlaying) this.stopAnimation();
      this.updateMarkerByProgress(val);
    },



    formatTime(timestamp) {
      const d = new Date(timestamp);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },

    handleAction(name) {
      this.$message.info(`点击了「${name}」按钮 (模拟)`);
    },
    onReplayModeChange(val) {
      if (!val) this.stopAnimation();
    },
  },
};
</script>

<style lang="less" scoped>
#Home .main-section>div,
#Home .main-section>.main {
  margin: 0px;
  min-height: calc(100vh - 107px);
}

.table-wrapper {
  height: 300px;

  /deep/ .el-table {
    .viewed {
      color: #989490;
    }

    .el-table-column--selection .cell {
      padding-left: 10px;
      padding-right: 10px;
    }

    .el-table__body-wrapper {
      &::-webkit-scrollbar {
        height: 10px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #ddd;
        border-radius: 5px;
      }
    }

    .el-table__fixed,
    .el-table__fixed-right {
      height: 100% !important;
    }
  }
}

/* ===== 整体布局 ===== */
.replay-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #f0f2f5;
  font-size: 13px;
  overflow: hidden;
}

.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ===== 左侧面板 ===== */
.left-panel {
  width: 340px;
  min-width: 340px;
  background: #fff;
  border-right: 1px solid #e4e7ed;
  padding: 10px 18px 10px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.vehicle-title .main-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

/* ===== 顶部筛选栏 ===== */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  // padding: 6px 0 10px 0;
  // border-bottom: 1px solid #e8edf3;
}

.filter-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.filter-left .el-select {
  width: 70px;
}

.filter-left .el-button {
  padding: 2px 10px;
  font-size: 14px;
}

.month-nav {
  display: flex;
  gap: 2px;
}

.filter-right {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.filter-right .el-button {
  padding: 6px 10px;
  font-size: 12px;
}

/* ===== 日历样式 ===== */
.calendar-wrapper {

  // margin-bottom: 8px;
  /deep/ .el-calendar {
    width: 96%;
    background: #fff;
    border-radius: 4px;
    border: 1px solid #e4e7ed;
    padding: 0 6px 6px;
  }

  /deep/ .el-calendar__header {
    display: none;
  }

  /deep/ .el-calendar__body {
    padding: 0;
  }

  /deep/ .el-calendar-table td {
    // padding: 2px 0;
  }

  /deep/ .el-calendar-table .el-calendar-day {
    height: auto;
    padding: 4px 0;
    line-height: 1.2;
    font-size: 13px;
    border-radius: 2px;

    &:hover {
      background: #ecf5ff;
    }
  }

  /deep/ .el-calendar-table td.is-today .el-calendar-day {
    background: #f0f9ff;

    .day-number {
      color: #409EFF;
      font-weight: 600;
    }
  }

  /deep/ .el-calendar-table td.is-selected .el-calendar-day {
    background: #409EFF;
    border-radius: 2px;

    .day-number,
    .day-mileage {
      color: #fff;
    }
  }

  /deep/ .el-calendar-table tr td:first-child .el-calendar-day .day-number {
    color: #f56c6c;
    /* 周日 */
  }

  /deep/ .el-calendar-table tr td:last-child .el-calendar-day .day-number {
    color: #f56c6c;
    /* 周六 */
  }

  .calendar-header {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4px 0 8px 0;

    .calendar-title {
      font-size: 13px;
      font-weight: 600;
      color: #303133;
    }
  }

  .calendar-day-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 33px;

    .day-number {
      font-size: 13px;
      font-weight: 500;
      color: #303133;
    }

    .day-mileage {
      font-size: 10px;
      color: #909399;
      margin-top: 2px;
      white-space: nowrap;
    }
  }
}

/* 时间选择器 */
.time-picker-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.time-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.time-item label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
  width: 70px;
}

.time-item .el-date-editor {
  flex: 1;
}

.params-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #e4e7ed;
}

.param-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.param-label {
  font-size: 13px;
  color: #606266;
}

.param-unit {
  font-size: 13px;
  color: #909399;
  margin-left: 2px;
}

.replay-switch {
  justify-content: flex-start;
}

/* ===== 右侧区域 ===== */
.right-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #e8edf3;
  overflow: hidden;
}

.map-container {
  position: relative;
  flex: 1;
  background: #e8edf3;
  overflow: hidden;
}

.map-container .amap-container {
  width: 100%;
  height: 100%;
}

.map-legend {
  position: absolute;
  bottom: 18px;
  left: 18px;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  padding: 6px 14px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #606266;
}

.map-legend span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.start-dot {
  background: #3b7cff;
}

.end-dot {
  background: #f56c6c;
}

.line-sample {
  display: inline-block;
  width: 20px;
  height: 3px;
  background: #3b7cff;
  border-radius: 2px;
}

.replay-bar {
  padding: 8px 20px;
  background: #fafbfc;
  border-top: 1px solid #e4e7ed;
  flex-shrink: 0;
}

.replay-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.time-label {
  font-size: 13px;
  color: #303133;
  min-width: 72px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}

.speed-label {
  font-size: 13px;
  color: #909399;
  margin-left: 4px;
}

/* ===== 底部表格 ===== */
.table-wrapper {
  flex: 0 0 auto;
  background: #fff;
  border-top: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  // max-height: 320px; // 取消固定高度，让表格自适应
}

.table-header {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  background: #f8f9fc;
  border-bottom: 1px solid #e4e7ed;
  flex-shrink: 0;
}

.table-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.table-sub {
  font-size: 13px;
  color: #3b7cff;
  margin-left: 12px;
  padding-left: 12px;
  border-left: 1px solid #e4e7ed;
}

.table-tools {
  margin-left: auto;
}

.table-wrapper .el-table {
  flex: 1;
  overflow: auto;
}

.table-wrapper .el-table th {
  background: #f5f7fa !important;
  color: #606266;
  font-weight: 600;
}

.table-wrapper .el-table .cell {
  font-size: 12px;
  padding: 0 6px;
}

.table-footer {
  padding: 4px 20px;
  background: #fafbfc;
  border-top: 1px solid #e4e7ed;
  font-size: 13px;
  color: #909399;
  text-align: right;
  flex-shrink: 0;
}

/* ===== 响应式 ===== */
@media screen and (max-width: 1024px) {
  .left-panel {
    width: 280px;
    min-width: 280px;
  }

  .time-item label {
    width: 60px;
  }
}

@media screen and (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }

  .left-panel {
    width: 100%;
    min-width: unset;
    max-height: 45vh;
    border-right: none;
    border-bottom: 1px solid #e4e7ed;
    padding: 12px 16px;
    gap: 12px;
  }

  .right-area {
    flex: 1;
    min-height: 300px;
  }

  .table-wrapper {
    max-height: 200px;
  }

  .replay-controls {
    flex-wrap: wrap;
    justify-content: center;
  }

  .time-label {
    min-width: 56px;
    font-size: 12px;
  }
}
</style>